import { useSyncExternalStore } from 'react';

import type { ColorScheme, ThemeInfo } from './types';

/**
 * Web useTheme — reads the `dark` class on `<html>` and stays in sync with
 * whatever toggles it (the app's ThemeProvider, devtools, …). Setter writes the
 * class directly; assumes Tailwind `darkMode: 'class'`.
 *
 * ONE MutationObserver for every consumer, attached while at least one is
 * mounted. It used to be one observer per hook instance, and shared cards call
 * this at several levels each (a FolderCard mounts ~8), so a library page held
 * hundreds of observers on the same attribute.
 *
 * SSR-safe: falls back to `light` when `document` is undefined.
 */
function readScheme(): ColorScheme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

function writeScheme(next: ColorScheme) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', next === 'dark');
}

const listeners = new Set<() => void>();
let observer: MutationObserver | null = null;

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  if (!observer && typeof document !== 'undefined') {
    observer = new MutationObserver(() => listeners.forEach((l) => l()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) {
      observer?.disconnect();
      observer = null;
    }
  };
}

export function useTheme(): ThemeInfo {
  const scheme = useSyncExternalStore(subscribe, readScheme, () => 'light' as const);
  return {
    scheme,
    isDark: scheme === 'dark',
    setScheme: (next) => writeScheme(next),
    toggle: () => writeScheme(readScheme() === 'dark' ? 'light' : 'dark'),
  };
}
